"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import { io, Socket } from "socket.io-client";

// Event callback types
type MessageCallback = (message: any) => void;
type ServerCallback = (server: any) => void;
type ChannelCallback = (channel: any) => void;
type MemberCallback = (data: { serverId: string; member: any }) => void;
type RoleCallback = (data: { serverId: string; role: any }) => void;
type TypingCallback = (data: { channelId: string; userId: string; username: string }) => void;
type PresenceCallback = (data: { userId: string; status: string }) => void;

export function useSocket(userId: string | undefined) {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  
  // Callback refs for all event types
  const callbacksRef = useRef<{
    message: MessageCallback | null;
    dm: MessageCallback | null;
    messageUpdate: MessageCallback | null;
    messageDelete: ((data: { channelId: string; messageId: string }) => void) | null;
    serverUpdate: ServerCallback | null;
    serverDelete: ((serverId: string) => void) | null;
    channelCreate: ChannelCallback | null;
    channelUpdate: ChannelCallback | null;
    channelDelete: ((data: { serverId: string; channelId: string }) => void) | null;
    memberJoin: MemberCallback | null;
    memberLeave: ((data: { serverId: string; userId: string }) => void) | null;
    memberUpdate: MemberCallback | null;
    roleCreate: RoleCallback | null;
    roleUpdate: RoleCallback | null;
    roleDelete: ((data: { serverId: string; roleId: string }) => void) | null;
    typing: TypingCallback | null;
    presence: PresenceCallback | null;
  }>({
    message: null,
    dm: null,
    messageUpdate: null,
    messageDelete: null,
    serverUpdate: null,
    serverDelete: null,
    channelCreate: null,
    channelUpdate: null,
    channelDelete: null,
    memberJoin: null,
    memberLeave: null,
    memberUpdate: null,
    roleCreate: null,
    roleUpdate: null,
    roleDelete: null,
    typing: null,
    presence: null,
  });

  useEffect(() => {
    if (!userId) return;

    const socket = io({ path: "/socket.io", transports: ["websocket", "polling"] });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Socket connected, identifying as:", userId);
      socket.emit("identify", { odId: userId });
      setIsConnected(true);
    });

    socket.on("disconnect", () => {
      console.log("Socket disconnected");
      setIsConnected(false);
    });

    // Message events
    socket.on("message", (data: any) => {
      callbacksRef.current.message?.(data);
    });

    socket.on("dm", (data: any) => {
      callbacksRef.current.dm?.(data);
    });

    socket.on("message-update", (data: any) => {
      callbacksRef.current.messageUpdate?.(data);
    });

    socket.on("message-delete", (data: any) => {
      callbacksRef.current.messageDelete?.(data);
    });

    // Server events
    socket.on("server-update", (data: any) => {
      callbacksRef.current.serverUpdate?.(data);
    });

    socket.on("server-delete", (data: any) => {
      callbacksRef.current.serverDelete?.(data);
    });

    // Channel events
    socket.on("channel-create", (data: any) => {
      callbacksRef.current.channelCreate?.(data);
    });

    socket.on("channel-update", (data: any) => {
      callbacksRef.current.channelUpdate?.(data);
    });

    socket.on("channel-delete", (data: any) => {
      callbacksRef.current.channelDelete?.(data);
    });

    // Member events
    socket.on("member-join", (data: any) => {
      callbacksRef.current.memberJoin?.(data);
    });

    socket.on("member-leave", (data: any) => {
      callbacksRef.current.memberLeave?.(data);
    });

    socket.on("member-update", (data: any) => {
      callbacksRef.current.memberUpdate?.(data);
    });

    // Role events
    socket.on("role-create", (data: any) => {
      callbacksRef.current.roleCreate?.(data);
    });

    socket.on("role-update", (data: any) => {
      callbacksRef.current.roleUpdate?.(data);
    });

    socket.on("role-delete", (data: any) => {
      callbacksRef.current.roleDelete?.(data);
    });

    // Typing indicator
    socket.on("typing", (data: any) => {
      callbacksRef.current.typing?.(data);
    });

    // Presence updates
    socket.on("presence", (data: any) => {
      callbacksRef.current.presence?.(data);
    });

    return () => {
      socket.disconnect();
    };
  }, [userId]);

  // Join/leave server room for real-time updates
  const joinServer = useCallback((serverId: string) => {
    socketRef.current?.emit("join-server", { serverId });
  }, []);

  const leaveServer = useCallback((serverId: string) => {
    socketRef.current?.emit("leave-server", { serverId });
  }, []);

  const joinChannel = useCallback((channelId: string) => {
    socketRef.current?.emit("join-channel", { channelId });
  }, []);

  const leaveChannel = useCallback((channelId: string) => {
    socketRef.current?.emit("leave-channel", { channelId });
  }, []);

  // Emit events
  const sendMessage = useCallback((channelId: string, message: any) => {
    socketRef.current?.emit("new-message", { channelId, message });
  }, []);

  const sendDM = useCallback((recipientId: string, message: any) => {
    socketRef.current?.emit("dm", { recipientId, message });
  }, []);

  const sendTyping = useCallback((channelId: string) => {
    socketRef.current?.emit("typing", { channelId });
  }, []);

  const updatePresence = useCallback((status: string) => {
    socketRef.current?.emit("presence", { status });
  }, []);

  // Event listeners - return cleanup function
  const createListener = useCallback(<T extends keyof typeof callbacksRef.current>(
    event: T,
    callback: NonNullable<typeof callbacksRef.current[T]>
  ) => {
    (callbacksRef.current as any)[event] = callback;
    return () => {
      (callbacksRef.current as any)[event] = null;
    };
  }, []);

  const onMessage = useCallback((cb: MessageCallback) => createListener('message', cb), [createListener]);
  const onDM = useCallback((cb: MessageCallback) => createListener('dm', cb), [createListener]);
  const onMessageUpdate = useCallback((cb: MessageCallback) => createListener('messageUpdate', cb), [createListener]);
  const onMessageDelete = useCallback((cb: (data: { channelId: string; messageId: string }) => void) => createListener('messageDelete', cb), [createListener]);
  const onServerUpdate = useCallback((cb: ServerCallback) => createListener('serverUpdate', cb), [createListener]);
  const onServerDelete = useCallback((cb: (serverId: string) => void) => createListener('serverDelete', cb), [createListener]);
  const onChannelCreate = useCallback((cb: ChannelCallback) => createListener('channelCreate', cb), [createListener]);
  const onChannelUpdate = useCallback((cb: ChannelCallback) => createListener('channelUpdate', cb), [createListener]);
  const onChannelDelete = useCallback((cb: (data: { serverId: string; channelId: string }) => void) => createListener('channelDelete', cb), [createListener]);
  const onMemberJoin = useCallback((cb: MemberCallback) => createListener('memberJoin', cb), [createListener]);
  const onMemberLeave = useCallback((cb: (data: { serverId: string; userId: string }) => void) => createListener('memberLeave', cb), [createListener]);
  const onMemberUpdate = useCallback((cb: MemberCallback) => createListener('memberUpdate', cb), [createListener]);
  const onRoleCreate = useCallback((cb: RoleCallback) => createListener('roleCreate', cb), [createListener]);
  const onRoleUpdate = useCallback((cb: RoleCallback) => createListener('roleUpdate', cb), [createListener]);
  const onRoleDelete = useCallback((cb: (data: { serverId: string; roleId: string }) => void) => createListener('roleDelete', cb), [createListener]);
  const onTyping = useCallback((cb: TypingCallback) => createListener('typing', cb), [createListener]);
  const onPresence = useCallback((cb: PresenceCallback) => createListener('presence', cb), [createListener]);

  return {
    socket: socketRef.current,
    isConnected,
    // Room management
    joinServer,
    leaveServer,
    joinChannel,
    leaveChannel,
    // Emit events
    sendMessage,
    sendDM,
    sendTyping,
    updatePresence,
    // Event listeners
    onMessage,
    onDM,
    onMessageUpdate,
    onMessageDelete,
    onServerUpdate,
    onServerDelete,
    onChannelCreate,
    onChannelUpdate,
    onChannelDelete,
    onMemberJoin,
    onMemberLeave,
    onMemberUpdate,
    onRoleCreate,
    onRoleUpdate,
    onRoleDelete,
    onTyping,
    onPresence,
  };
}
